/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { tb } from 'components/layouts/Table';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomTextarea from 'components/inputs/CustomTextarea';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { useLoading } from 'components/process/Loading';
import useSessionStore from 'stores/useSessionStore';
import { PageMode } from 'models/common/Common';
import { CrudCode } from 'models/common/Edit';
import { SetupProject } from 'models/setup/SetupProject';
import { findDisplayProject } from 'apis/setup/SetupProject';
import { findExcpByElmPrjId } from 'apis/setup/SetupSchedule';
import {
  completePrjSchedule,
  completePrjScheduleTgtEqp,
  findPrjSchedule,
  findPrjTargetEquipment,
  savePrjSchedule,
} from 'apis/setup/PrjSchedule';
import {
  PrjSchedule,
  PRJ_SCHD_WKT_TP_CD,
  PrjScheduleBox,
  PrjScheduleEqp,
} from 'models/setup/PrjSchedule';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { IconButton } from 'components/buttons/IconSVG';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomDateTimePicker from 'components/inputs/CustomDateTimepicker';
import CustomGrid from 'components/grids/CustomGrid';
import PrjScheduleBoxPopup from './PrjScheduleBoxPopup';
import _ from 'lodash';

type Props = {
  open: boolean;
  close: () => void;
  schdId?: string;
  defaultDate?: string;
};

/**
 * @desc 셋업프로젝트 > 일정관리 > 일정 등록 (등록/수정 겸용)
 *       표 형식 폼 — 프로젝트/업무유형/협력업체/대상설비 · 제목/기간 · 상세내용 · 진행사항 ·
 *       상태/등록자/등록일자(Read only), 저장/완료 처리/목록 버튼
 * @author cnsglee
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.03   cnsglee        신규 생성 (외부 개발본 elm_dev ScheduleRegisterPage 이관)
 */
const PrjScheduleRegistPopup = ({ open, close, schdId, defaultDate }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { openLoading } = useLoading();
  const { userId, empNm } = useSessionStore();

  const [pageMode, setPageMode] = useState(schdId ? PageMode.READ : PageMode.WRITE);

  const gridRef = useRef<any>();
  const [errors, setErrors] = useState<any>({});
  const [createData, setCreateData] = useState<PrjSchedule>();
  const [originData, setOriginData] = useState<PrjSchedule>();
  const [isOpenSchdBoxPopup, setIsOpenSchdBoxPopup] = useState(false);

  // 공통코드/참조 콤보
  const [wktTpCodes, setWktTpCodes] = useState<Code[]>([]);
  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);
  const [prjExcpData, setPrjExcpData] = useState<any[]>([]);
  const [tgtEqpData, setTgtEqpData] = useState<PrjScheduleEqp[]>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'pakBoxNo',
        header: String(t('com.label.Box No', 'Box No')),
        minWidth: 80,
        width: 120,
        align: 'center',
      },
      {
        binding: 'pakItemNm',
        header: String(t('com.label.품목 목록명', '품목 목록명')),
        minWidth: 80,
        width: '*',
        align: 'left',
      },
      {
        binding: 'mcodes',
        header: String(t('com.label.M-Code List', 'M-Code List')),
        minWidth: 80,
        width: 180,
        align: 'center',
      },
      {
        binding: 'bcdYn',
        header: String(t('com.label.바코드 관리', '바코드 관리')),
        minWidth: 80,
        width: 180,
        align: 'center',
      },
      {
        header: String(t('com.label.진행현황', '진행현황')),
        columns: [
          {
            binding: 'boxStatNm',
            header: String(t('com.label.현재상태', '현재상태')),
            minWidth: 80,
            width: 150,
            align: 'center',
          },
          {
            binding: 'boxUnloDtm',
            header: String(t('com.label.도착처리', '도착처리')),
            minWidth: 80,
            width: 150,
            align: 'center',
          },
          {
            binding: 'inptDtm',
            header: String(t('com.label.Fab-In', 'Fab-In')),
            minWidth: 80,
            width: 150,
            align: 'center',
          },
          {
            binding: 'abnormalYn',
            header: String(t('com.label.이상처리', '이상처리')),
            minWidth: 80,
            width: 150,
            align: 'center',
          },
        ],
      },
    ];
  }, [createData, pageMode]);

  const tgtEqpLayoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnProcCdNm',
        header: String(t('com.label.공정', '공정')),
        minWidth: 80,
        width: '*',
        align: 'center',
      },
      {
        binding: 'mcode',
        header: String(t('com.label.M Code', 'M Code')),
        minWidth: 80,
        width: '*',
        align: 'center',
      },
      {
        binding: 'eqpNm',
        header: String(t('com.label.설비명', '설비명')),
        minWidth: 80,
        width: '*',
        align: 'center',
      },
      {
        binding: 'cpltYn',
        header: String(t('com.label.상태', '상태')),
        minWidth: 80,
        width: 140,
        align: 'center',
        cssClass: 'WijmoBasicButton noTooltip WijmoTag',
        cellTemplate: (ctx) => {
          if (!ctx.item.schdId) return;

          if (ctx.value === 'Y') {
            return `<span class='green'>완료</span>`;
          } else {
            return pageMode !== PageMode.READ ? `<button class='cplt'>완료 처리</button>` : '';
          }
        },
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.완료일자', '완료일자')),
        minWidth: 80,
        width: '*',
        align: 'center',
        cellTemplate: (ctx) => {
          if (ctx.item.cpltYn === 'Y') {
            return `<span>${ctx.value}</span>`;
          } else {
            return ``;
          }
        },
      },
    ];
  }, [createData, pageMode]);

  const optionsPrjMaster = useMemo(
    () => (prjMasterData || []).map((o) => ({ cmnCd: o.elmPrjId, cmnCdNm: o.elmPrjNm } as Code)),
    [prjMasterData]
  );
  const optionsPrjExcp = useMemo(
    () => (prjExcpData || []).map((o) => ({ cmnCd: o.excpId, cmnCdNm: o.excpNm } as Code)),
    [prjExcpData]
  );
  const optionsTgtEqp = useMemo(
    () => (tgtEqpData || []).map((o) => ({ cmnCd: o.tgtEqpId, cmnCdNm: `${o.eqpNm}` } as Code)),
    [tgtEqpData]
  );

  // 완료 상태
  const isStatCplt = () => {
    return schdId && originData?.schdStatCd === 'CPLT';
  };

  const handleOnChange = (name: keyof PrjSchedule, value: any) => {
    setCreateData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    if (!createData?.elmPrjId) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.프로젝트를 선택하세요.', '프로젝트를 선택하세요.')),
      });
      return;
    }
    if (!createData?.excpId) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.협력업체를 선택하세요.', '협력업체를 선택하세요.')),
      });
      return;
    }
    if (!createData?.tgtEqpList?.length) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.대상 설비를 선택하세요.', '대상 설비를 선택하세요.')),
      });
      return;
    }

    setIsOpenSchdBoxPopup(true);
  };

  /** 필수값 검증 */
  const validate = (): boolean | string => {
    const nextErrors: any = {};
    if (!createData?.elmPrjId) nextErrors.elmPrjId = true;
    if (!createData?.wktTpCd) nextErrors.wktTpCd = true;
    if (!createData?.excpId) nextErrors.excpId = true;
    if (!createData?.tgtEqpList?.length) nextErrors.tgtEqpList = true;
    if (!createData?.schdNm?.trim()) nextErrors.schdNm = true;
    if (!createData?.planStDtm) nextErrors.planStDtm = true;
    if (
      !createData?.planEndDtm ||
      (createData?.planStDtm &&
        createData?.planEndDtm &&
        createData.planEndDtm < createData.planStDtm)
    ) {
      nextErrors.planEndDtm = true;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  /** 저장 (등록/수정) */
  const handleSave = () => {
    if (!validate()) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.필수값을 확인해 주세요.', '필수값을 확인해 주세요.')),
      });
      return;
    }

    // 대상설비가 포함되는 box가 있는지 체크
    if (
      createData?.wktTpCd === 'CARRY_IN' &&
      !createData?.tgtEqpList?.every((o) =>
        createData?.boxList?.some((x) => x.mcodes?.includes(o.eqpNm || ''))
      )
    ) {
      openMessageBar({
        type: 'error',
        content: String(
          t(
            'com.msg.대상설비 중 Box를 추가하지 않은 설비가 있습니다.',
            '대상설비 중 Box를 추가하지 않은 설비가 있습니다.'
          )
        ),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: String(t('com.msg.저장하시겠습니까?', '저장하시겠습니까?')),
      yesCallback: async () => {
        openLoading(true);
        try {
          const response = await savePrjSchedule({
            ...createData,
            schdNm: createData?.schdNm?.trim(),
            crudKey: schdId ? CrudCode.UPDATE : CrudCode.CREATE,
          });
          if (response?.successOrNot === 'Y') {
            openMessageBar({
              type: 'confirm',
              content: String(t('com.msg.저장되었습니다.', '저장되었습니다.')),
            });
            close();
          } else {
            openMessageBar({
              type: 'error',
              content: String(t('com.msg.저장에 실패했습니다.', '저장에 실패했습니다.')),
            });
          }
        } finally {
          openLoading(false);
        }
      },
    });
  };

  /** 일정 삭제 */
  const handleDelete = () => {
    openCommonModal({
      modalType: 'confirm',
      content: String(t('com.msg.일정을 삭제하시겠습니까?', '일정을 삭제하시겠습니까?')),
      yesCallback: async () => {
        openLoading(true);
        try {
          const response = await savePrjSchedule({
            ...createData,
            crudKey: CrudCode.DELETE,
          });
          if (response?.successOrNot === 'Y') {
            openMessageBar({
              type: 'confirm',
              content: String(t('com.msg.삭제되었습니다.', '삭제되었습니다.')),
            });
            close();
          } else {
            openMessageBar({
              type: 'error',
              content: String(t('com.msg.삭제에 실패했습니다.', '삭제에 실패했습니다.')),
            });
          }
        } finally {
          openLoading(false);
        }
      },
    });
  };

  /** 완료 처리 (SCHD_STAT_CD → CPLT) */
  const handleComplete = () => {
    if (!createData?.schdId) return;

    openCommonModal({
      modalType: 'confirm',
      content: String(t('setup.msg.완료 처리하시겠습니까?', '완료 처리하시겠습니까?')),
      yesCallback: async () => {
        const response = await completePrjSchedule(createData.schdId as string);
        if (response?.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: String(t('setup.msg.완료 처리되었습니다.', '완료 처리되었습니다.')),
          });
          close();
        } else {
          openMessageBar({
            type: 'error',
            content: String(t('com.msg.완료 처리에 실패했습니다.', '완료 처리에 실패했습니다.')),
          });
        }
      },
    });
  };

  const handleAllComplete = () => {
    if (!createData?.schdId) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.일정을 등록해주세요.', '일정을 등록해주세요.')),
      });
      return;
    }
    const checkedList: PrjScheduleEqp[] = gridRef.current.rows
      .filter((o) => o.isSelected)
      .map((o) => o.dataItem);
    if (!checkedList?.length) {
      openMessageBar({
        type: 'error',
        content: String(t('com.msg.선택된 항목이 없습니다.', '선택된 항목이 없습니다.')),
      });
      return;
    } else if (checkedList.some((o) => o.cpltYn === 'Y')) {
      openMessageBar({
        type: 'error',
        content: String(
          t('com.msg.완료되지 않은 항목만 선택해주세요.', '완료되지 않은 항목만 선택해주세요.')
        ),
      });
      return;
    }

    openCommonModal({
      modalType: 'confirm',
      content: String(t('setup.msg.완료 처리하시겠습니까?', '완료 처리하시겠습니까?')),
      yesCallback: async () => {
        const response = await completePrjSchedule(createData?.schdId as string);
        if (response?.successOrNot === 'Y') {
          openMessageBar({
            type: 'confirm',
            content: String(t('setup.msg.완료 처리되었습니다.', '완료 처리되었습니다.')),
          });
          close();
        } else {
          openMessageBar({
            type: 'error',
            content: String(t('com.msg.완료 처리에 실패했습니다.', '완료 처리에 실패했습니다.')),
          });
        }
      },
    });
  };

  const callbackFn = (items: PrjScheduleBox[]) => {
    const boxList = items.map((o) => ({ ...o }));
    setCreateData((prev) => ({ ...prev, boxList }));
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        if (e.target instanceof HTMLButtonElement) {
          if (e.target.classList.contains('cplt')) {
            completePrjScheduleTgtEqp([item])
              .then((res) => {
                if (res.successOrNot === 'Y') {
                  gridRef.current.rows.forEach((r) => {
                    if (r.dataItem.tgtEqpId === item.tgtEqpId) {
                      r.dataItem.cpltYn = 'Y';
                      r.dataItem.dataUpdDtm = dayjs().format('YYYY-MM-DD');
                    }
                  });
                  gridRef.current.refresh();

                  openMessageBar({
                    type: 'confirm',
                    content: String(t('com.msg.완료되었습니다.', '완료되었습니다.')),
                  });
                } else {
                  openMessageBar({
                    type: 'error',
                    content: String(
                      t('com.msg.완료 처리에 실패했습니다.', '완료 처리에 실패했습니다.')
                    ),
                  });
                }
              })
              .catch((err) => {
                openMessageBar({
                  type: 'error',
                  content: String(
                    t('com.msg.완료 처리에 실패했습니다.', '완료 처리에 실패했습니다.')
                  ),
                });
              });
          }
        }
      }
    });
  };

  useEffect(() => {
    (async () => {
      setWktTpCodes((await getCommonCodeNames(PRJ_SCHD_WKT_TP_CD)) || []);
      setPrjMasterData((await findDisplayProject()) || []);
    })();
  }, []);

  useEffect(() => {
    if (schdId) {
      (async () => {
        const data = (await findPrjSchedule(schdId)).data || undefined;
        setCreateData(_.cloneDeep(data));
        setOriginData(_.cloneDeep(data));
      })();
    } else {
      // 신규 입력시 날짜 default 세팅
      const initialDate = dayjs(defaultDate);
      setCreateData({
        planStDtm: initialDate.format('YYYY-MM-DD HH:mm'),
        planEndDtm: initialDate.add(1, 'hour').format('YYYY-MM-DD HH:mm'),
      });
      setOriginData({
        planStDtm: initialDate.format('YYYY-MM-DD HH:mm'),
        planEndDtm: initialDate.add(1, 'hour').format('YYYY-MM-DD HH:mm'),
      });
    }
  }, [schdId, defaultDate]);

  // 프로젝트/협력업체 선택 → 협력업체 콤보 재조회
  useEffect(() => {
    (async () => {
      if (!createData?.elmPrjId) {
        setPrjExcpData([]);
        setTgtEqpData([]);
        return;
      }
      setPrjExcpData((await findExcpByElmPrjId(createData.elmPrjId)) || []);
    })();
  }, [createData?.elmPrjId, createData?.wktTpCd]);

  // 프로젝트/협력업체 선택 → 대상설비 콤보 재조회
  useEffect(() => {
    (async () => {
      if (!createData?.elmPrjId) {
        setPrjExcpData([]);
        setTgtEqpData([]);
        return;
      } else if (!createData?.excpId) {
        setTgtEqpData([]);
        return;
      }
      // 대상 설비: EQP_TP_CD='EQP', 협력업체 선택 시 해당 업체 담당 설비만
      setTgtEqpData(
        (await findPrjTargetEquipment(
          createData.elmPrjId,
          createData?.excpId,
          createData?.wktTpCd || ''
        )) || []
      );
    })();
  }, [createData?.elmPrjId, createData?.wktTpCd, createData?.excpId]);

  return (
    <>
      <CustomDialog
        title={
          schdId
            ? String(t('setup.label.일정 상세', '일정 상세'))
            : String(t('setup.label.일정 등록', '일정 등록'))
        }
        size={'xl'}
        open={open}
        onClose={close}
      >
        <TableContainer css={tb.table}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="label">
                  <span className="dot">{String(t('setup.label.프로젝트', '프로젝트'))}</span>
                </TableCell>
                <TableCell colSpan={3}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span>
                      {optionsPrjMaster.find((o) => o.cmnCd === createData?.elmPrjId)?.cmnCdNm}
                    </span>
                  ) : (
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={optionsPrjMaster}
                      defaultValue={createData?.elmPrjId}
                      onChange={(value) => {
                        handleOnChange('elmPrjId', value);
                        handleOnChange('excpId', '');
                        handleOnChange('tgtEqpList', []);
                      }}
                      isError={errors?.elmPrjId}
                      msgError={String(
                        t('setup.label.프로젝트를 선택해 주세요.', '프로젝트를 선택해 주세요.')
                      )}
                    />
                  )}
                </TableCell>
                <TableCell className="label">
                  <span className="dot">{t('setup.label.협력업체', '협력업체')}</span>
                </TableCell>
                <TableCell>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span>
                      {optionsPrjExcp.find((o) => o.cmnCd === createData?.excpId)?.cmnCdNm}
                    </span>
                  ) : (
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={optionsPrjExcp}
                      defaultValue={createData?.excpId}
                      onChange={(value) => {
                        // 협력업체 변경 시 대상 설비 초기화 (해당 업체 담당 설비로 재조회)
                        handleOnChange('excpId', value);
                        handleOnChange('tgtEqpList', []);
                      }}
                      isError={errors?.excpId}
                      msgError={String(
                        t('setup.label.협력업체를 선택해 주세요.', '협력업체를 선택해 주세요.')
                      )}
                    />
                  )}
                </TableCell>
                <TableCell className="label">
                  <span className="dot">{t('setup.label.대상설비', '대상설비')}</span>
                </TableCell>
                <TableCell style={{ maxWidth: '300px', whiteSpace: 'normal' }}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span>{createData?.tgtEqpList?.map((o) => o.eqpNm).join(', ')}</span>
                  ) : (
                    <MultiComboBox
                      id="tgtEqpList"
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={optionsTgtEqp}
                      defaultValue={createData?.tgtEqpList?.map((o) => o.tgtEqpId || '') || []}
                      onChange={(value) =>
                        handleOnChange(
                          'tgtEqpList',
                          tgtEqpData.filter((o) => value.includes(o.tgtEqpId || ''))
                        )
                      }
                      isError={errors?.tgtEqpList}
                      msgError={String(
                        t('setup.label.대상설비를 선택해 주세요.', '대상설비를 선택해 주세요.')
                      )}
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="label">
                  <span className="dot">{t('setup.label.업무유형', '업무유형')}</span>
                </TableCell>
                <TableCell style={{ minWidth: '80px' }}>
                  {pageMode !== PageMode.WRITE ? (
                    <span>{createData?.wktTpCdNm}</span>
                  ) : (
                    <ComboBox
                      placeholder={String(t('com.label.선택', '선택'))}
                      options={wktTpCodes}
                      defaultValue={createData?.wktTpCd}
                      onChange={(value) => handleOnChange('wktTpCd', value)}
                      isError={errors?.wktTpCd}
                      msgError={String(
                        t('setup.label.업무유형을 선택해 주세요.', '업무유형을 선택해 주세요.')
                      )}
                    />
                  )}
                </TableCell>
                <TableCell className="label">
                  <span className="dot">{t('setup.label.제목', '제목')}</span>
                </TableCell>
                <TableCell colSpan={3}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span>{createData?.schdNm}</span>
                  ) : (
                    <CustomInputWithSearch
                      value={createData?.schdNm}
                      onChange={(e) => handleOnChange('schdNm', e.target.value)}
                      placeholder={String(
                        t('setup.label.일정 제목을 입력해 주세요.', '일정 제목을 입력해 주세요.')
                      )}
                      isError={errors?.schdNm}
                      msgError={String(
                        t('setup.label.제목을 입력해 주세요.', '제목을 입력해 주세요.')
                      )}
                    />
                  )}
                </TableCell>
                <TableCell className="label">
                  <span className="dot">{t('setup.label.기간', '기간')}</span>
                </TableCell>

                <TableCell colSpan={3}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span>{`${createData?.planStDtm}  - ${createData?.planEndDtm}`}</span>
                  ) : (
                    <CustomDateTimePicker
                      isRange={true}
                      value={[createData?.planStDtm || '', createData?.planEndDtm || '']}
                      onChange={([start, end]: [string, string]) => {
                        handleOnChange('planStDtm', start);
                        handleOnChange('planEndDtm', end);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>

              {/* 3행: 상세내용 (크게) */}
              <TableRow>
                <TableCell className="label">{t('setup.label.상세내용', '상세내용')}</TableCell>
                <TableCell colSpan={7}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span
                      style={{
                        minHeight: '100px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {createData?.schdCtn}
                    </span>
                  ) : (
                    <CustomTextarea
                      value={createData?.schdCtn}
                      onChange={(e) => handleOnChange('schdCtn', e.target.value)}
                      placeholder={String(
                        t(
                          'setup.label.일정 상세내용을 입력해 주세요.',
                          '일정 상세내용을 입력해 주세요.'
                        )
                      )}
                      wrapStyle={{ minHeight: '100px' }}
                    />
                  )}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="label">{t('setup.label.진행사항', '진행사항')}</TableCell>
                <TableCell colSpan={7}>
                  {pageMode === PageMode.READ || isStatCplt() ? (
                    <span
                      style={{
                        minHeight: '70px',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {createData?.progMatrCtn}
                    </span>
                  ) : (
                    <CustomTextarea
                      value={createData?.progMatrCtn}
                      onChange={(e) => handleOnChange('progMatrCtn', e.target.value)}
                      placeholder={String(
                        t(
                          'setup.label.일정 상세내용을 입력해 주세요.',
                          '일정 상세내용을 입력해 주세요.'
                        )
                      )}
                      wrapStyle={{ minHeight: '70px' }}
                    />
                  )}
                </TableCell>
              </TableRow>

              {/* Read only: 상태 / 등록자 / 등록일자 */}
              <TableRow>
                <TableCell className="label">{t('setup.label.상태', '상태')}</TableCell>
                <TableCell>
                  <div className="txt">
                    <span>
                      {isStatCplt() ? t('setup.label.완료', '완료') : t('setup.label.등록', '등록')}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="label">{t('setup.label.등록자', '등록자')}</TableCell>
                <TableCell>
                  <div className="txt">
                    <span>{createData?.dataInsUserNm || empNm}</span>
                  </div>
                </TableCell>
                <TableCell className="label">{t('setup.label.등록일자', '등록일자')}</TableCell>
                <TableCell colSpan={3}>
                  <div className="txt">
                    <span>
                      {dayjs(createData?.dataInsDtm).format('YYYY-MM-DD') ||
                        dayjs().format('YYYY-MM-DD')}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {createData?.wktTpCd === 'CARRY_IN' ? (
          <>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('pjm.label.품목 Box 목록', '품목 Box 목록')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{createData?.boxList?.length ?? 0}</span>
                  {t('com.label.건', '건')}
                </span>
              </SubTitleGroup>
              <ControlBtnGroup>
                {pageMode !== PageMode.READ && !isStatCplt() && (
                  <Button
                    css={IconButton.button}
                    className="plus"
                    disableRipple
                    onClick={handleAdd}
                  >
                    {t('com.button.Box 관리', 'Box 관리')}
                  </Button>
                )}
              </ControlBtnGroup>
            </SubTitleLayout>
            <CustomGrid
              layoutDefinition={layoutDefinition}
              rowData={createData?.boxList || []}
              isReadOnly
              height={240}
              initialized={onInitialized}
            />
          </>
        ) : (
          <>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('pjm.label.대상설비 목록', '대상설비 목록')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{createData?.tgtEqpList?.length ?? 0}</span>
                  {t('com.label.건', '건')}
                </span>
              </SubTitleGroup>
              <ControlBtnGroup>
                {pageMode !== PageMode.READ && !isStatCplt() && createData?.schdId && (
                  <Button
                    css={IconButton.button}
                    className="icoNone"
                    disableRipple
                    onClick={handleAllComplete}
                  >
                    {t('com.button.일괄 완료처리', '일괄 완료처리')}
                  </Button>
                )}
              </ControlBtnGroup>
            </SubTitleLayout>
            <CustomGrid
              layoutDefinition={tgtEqpLayoutDefinition}
              rowData={createData?.tgtEqpList || []}
              isReadOnly
              height={240}
              initialized={onInitialized}
            />
          </>
        )}

        <GlobalBtnGroup>
          {pageMode === PageMode.EDIT && (
            <Button
              css={IconButton.button}
              className="cancel"
              disableRipple
              onClick={() => setPageMode(PageMode.READ)}
            >
              {t('com.button.취소', '취소')}
            </Button>
          )}
          {/* {pageMode === PageMode.EDIT && isCompleteBtn() && (
            <Button
              css={IconButton.button}
              className="icoNone"
              disableRipple
              onClick={handleComplete}
            >
              {t('setup.button.완료 처리', '완료 처리')}
            </Button>
          )} */}
          {(pageMode === PageMode.EDIT || isStatCplt()) && (
            <Button css={IconButton.button} className="delete" disableRipple onClick={handleDelete}>
              {t('com.button.삭제', '삭제')}
            </Button>
          )}
          {pageMode !== PageMode.READ && !isStatCplt() && (
            <Button css={IconButton.button} className="save" disableRipple onClick={handleSave}>
              {t('com.button.저장', '저장')}
            </Button>
          )}
          {pageMode === PageMode.READ && !isStatCplt() && (
            <Button
              css={IconButton.button}
              className="icoNone"
              disableRipple
              onClick={() => setPageMode(PageMode.EDIT)}
            >
              {t('com.button.수정', '수정')}
            </Button>
          )}
        </GlobalBtnGroup>
      </CustomDialog>

      {isOpenSchdBoxPopup && (
        <PrjScheduleBoxPopup
          open={isOpenSchdBoxPopup}
          close={() => setIsOpenSchdBoxPopup(false)}
          elmPrjId={createData?.elmPrjId || ''}
          tgtEqpId={createData?.tgtEqpList?.map((o) => o.tgtEqpId).join(',') || ''}
          selectedData={createData?.boxList || []}
          callbackFn={callbackFn}
        />
      )}
    </>
  );
};

export default PrjScheduleRegistPopup;
